import { useRef } from 'react';

interface Props<T extends string> {
  readonly tabs: ReadonlyArray<{ readonly id: T; readonly label: string; /** Pestaña que aún no está disponible: se muestra atenuada y no se puede elegir. */ readonly disabled?: boolean }>;
  readonly value: T;
  readonly onChange: (id: T) => void;
  /** Prefijo de los ids: la pestaña `x` es `${idPrefix}-tab-x` y controla `${idPrefix}-panel`. */
  readonly idPrefix: string;
  readonly label: string;
}

/** Pestañas con el patrón ARIA «tabs»: flechas, Inicio y Fin, con activación automática. */
export function Tabs<T extends string>({ tabs, value, onChange, idPrefix, label }: Props<T>): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(e.key)) return;
    e.preventDefault();
    const on = tabs.map((t, i) => (t.disabled === true ? -1 : i)).filter((i) => i >= 0);
    const at = on.indexOf(tabs.findIndex((t) => t.id === value));
    const next = e.key === 'Home' ? on[0]! : e.key === 'End' ? on[on.length - 1]! : on[(at + (e.key === 'ArrowRight' ? 1 : -1) + on.length) % on.length]!;
    onChange(tabs[next]!.id);
    ref.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]?.focus();
  };
  return (
    <div className="subtabs" role="tablist" aria-label={label} ref={ref} onKeyDown={onKeyDown}>
      {tabs.map((t) => (
        <button key={t.id} type="button" role="tab" id={`${idPrefix}-tab-${t.id}`} aria-selected={t.id === value} aria-controls={`${idPrefix}-panel`} aria-disabled={t.disabled === true ? 'true' : undefined} disabled={t.disabled === true} tabIndex={t.id === value ? 0 : -1} onClick={() => onChange(t.id)}>
          {t.label}
        </button>
      ))}
    </div>
  );
}
